import { createElement, type FunctionComponent, type ReactElement } from 'react';

import { PaginaForaDoAlcance, PaginaSemPermissao } from '@/componentes/SemPermissao';
import { fraseDaEmpresaInteira } from '@/modulos/acesso/alcance';
import { useSessao } from '@/modulos/sessao/consultas';

/**
 * A tela que pede LEITURA própria, guardada na rota: sem a licença, a página
 * sem permissão com o perfil a pedir, e nenhuma consulta disparada.
 *
 * Achado da prova do marco do ciclo 1 (07/10/2026): pelo endereço, o operador
 * abria o estoque e via "Não foi possível carregar o estoque — Carregar de
 * novo", e a manutenção com "0 de 0 tarefas" ao lado da recusa. O 403 é o
 * quinto estado da tela (p.34), e não o erro: carregar de novo não concede
 * licença. O menu já escondia o módulo (`modulosVisiveis`); o endereço, não.
 *
 * A licença é a da ROTA de leitura que a tela consulta primeiro (`config.exige`
 * no backend) — por tela, e não por módulo do menu: a inspeção e a O.S. moram
 * no mesmo item do menu e pedem licenças diferentes.
 *
 * A licença vem de `LEITURA_DA_TELA` (`src/modulos/acesso/telas.ts`), a mesma
 * tabela que recorta o menu: a tela que o menu oferece é a que a rota abre
 * (robôs no QAS, 20/10/2026; `src/componentes/menu-segue-a-tela.test.ts`).
 */
export interface TelaComLeitura {
  readonly licenca: string;
  /** O que a licença libera, com o artigo: "o estoque", "os custos". */
  readonly modulo: string;
  readonly sobretitulo: string;
  readonly titulo: string;
}

/* `Pagina` entra sem tipo de propósito: as páginas leem o tipo do roteador
   (`getRouteApi`), e o roteador leria o tipo delas por aqui — um ciclo que o
   TypeScript resolve virando `any` nas buscas de todas as telas. */
export function comLeitura(Pagina: unknown, tela: TelaComLeitura): () => ReactElement {
  function ComLeitura(): ReactElement {
    const sessao = useSessao();
    /* Sessão em trânsito: a página segue (ela tem o próprio esqueleto). */
    if (sessao.data && !sessao.data.permissoes.includes(tela.licenca)) return createElement(PaginaSemPermissao, tela);
    return createElement(Pagina as FunctionComponent);
  }
  return ComLeitura;
}

/**
 * A TELA DA EMPRESA INTEIRA, guardada na rota (robôs, rodada 3): com acesso a
 * só parte da empresa, o estado próprio com a frase do servidor, e nenhuma
 * consulta disparada — o menu já não oferece a tela; o endereço direto, sim.
 */
export function soNaEmpresaInteira(
  Pagina: unknown,
  tela: { readonly caminho: string; readonly sobretitulo: string; readonly titulo: string },
): () => ReactElement {
  const frase = fraseDaEmpresaInteira(tela.caminho);
  if (frase === null) throw new Error(`${tela.caminho} não está em TELAS_DA_EMPRESA_INTEIRA (acesso/alcance.ts)`);
  function NaEmpresaInteira(): ReactElement {
    const sessao = useSessao();
    if (sessao.data?.alcanceParcial === true) {
      return createElement(PaginaForaDoAlcance, { sobretitulo: tela.sobretitulo, titulo: tela.titulo, frase: frase ?? '' });
    }
    return createElement(Pagina as FunctionComponent);
  }
  return NaEmpresaInteira;
}
